import React from "react";
import { GeneralButton, GeneralInput, Modal } from "../../../components";
import { Link, useNavigate } from "react-router-dom";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, usePostData } from "../../../hooks"; 
import { EndPoints } from "../../../Constants/endpoints";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { setOldPassword } from "../../../redux/reducers/changePasswordSlice";
import { useToast } from "../../../Services/ToastContext";

function ChangePasswordModal({
  isChangePasswordModalOpen,
  onClose,
}: {
  isChangePasswordModalOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { mutate } = usePostData(EndPoints.CheckPassword);
  const passwordSchema = z.object({
    Password: z.string().min(8, { message: t("password_minimum_length") }),
  });

  const changePasswordMethods = useForm<{ Password: string }>({
    resolver: zodResolver(passwordSchema),
  });
  const { handleSubmit: handleChangePasswordSubmit } = changePasswordMethods;

  const onCheckPasswordSubmit: SubmitHandler<{ Password: string }> = (data) => {
    dispatch(setOldPassword(data.Password));
    mutate(
      { Password: data.Password },
      {
        onSuccess: (data) => {
          if (data.Status) {
            showToast(t("password_verified"), "success");
            navigate("/change-password");
          } else {
            showToast(data.ErrorMessage || t("incorrect_password"), "error");
          }
        },
        onError: (error: any) => {
          showToast(
            error.response?.data?.ErrorMessage || error.message,
            "error",
          );
        },
      },
    );
  };
  return (
    <Modal
      isOpen={isChangePasswordModalOpen}
      onClose={onClose}
      header={t("please_enter_your_old_password")}
      headerCustomStyle="!mb-2"
      customStyle="max-w-3xl"
    >
      <FormProvider {...changePasswordMethods}>
        <form
          onSubmit={handleChangePasswordSubmit(onCheckPasswordSubmit)}
          className="flex flex-col"
        >
          <p className="mb-8 text-body-text">
            {t("to_change_your_password_you_must_enter_the_old_one")}
          </p>

          <GeneralInput
            name="Password"
            type="password"
            label={t("old_password")}
            placeholder={t("old_password")}
            required
          />
          <Link
            to="/forget_password/setting"
            className="hover:text-600 mt-4 text-end font-bold underline"
          >
            {t("forgot_password_question")}
          </Link>
          <div className="mt-6 flex justify-end gap-2">
            <GeneralButton 
              title={t("cancel")}
              color="liteGray"
              onClick={onClose}
            />
            <GeneralButton title={t("next")} type="submit" className="!px-5" />
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}

export default ChangePasswordModal;
